import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import "./LandingPage.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCAN_MODULES = ["Design", "Mobile", "Speed", "Trust", "SEO", "Conversion", "Accessibility", "AI Readiness"];

const GALLERY_ITEMS = [
  { name: "Athens Plumbing", industry: "Plumbing · Athens, GA", tag: "Service", before: 31, after: 96, metric: "+210% calls" },
  { name: "Bright Smile Dental", industry: "Dentistry · Tampa, FL", tag: "Healthcare", before: 38, after: 94, metric: "+45% bookings" },
  { name: "Summit Roofing", industry: "Roofing · Denver, CO", tag: "Trades", before: 22, after: 97, metric: "+180% leads" },
  { name: "Garcia Auto Repair", industry: "Auto · Houston, TX", tag: "Service", before: 28, after: 93, metric: "+90% calls" },
  { name: "Bella Vita Med Spa", industry: "Med Spa · Scottsdale", tag: "Wellness", before: 35, after: 98, metric: "+120% bookings" },
  { name: "Peterson Family Law", industry: "Law · Charlotte, NC", tag: "Professional", before: 41, after: 95, metric: "+65% consults" },
];

/* ═══════════════════════════════════════════════════════
   TYPEWRITER — types and optionally deletes text
   ═══════════════════════════════════════════════════════ */
function Typewriter({ text, speed = 40, onDone, startDelay = 0, className = "" }) {
  const [displayed, setDisplayed] = useState("");
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const d = setTimeout(() => setStarted(true), startDelay);
    return () => clearTimeout(d);
  }, [startDelay]);

  useEffect(() => {
    if (!started) return;
    if (displayed.length < text.length) {
      const t = setTimeout(() => setDisplayed(text.slice(0, displayed.length + 1)), speed);
      return () => clearTimeout(t);
    } else if (onDone) {
      const t = setTimeout(onDone, 200);
      return () => clearTimeout(t);
    }
  }, [displayed, text, speed, onDone, started]);

  return <span className={className}>{displayed}<span className="xp-cursor">|</span></span>;
}

/* ═══════════════════════════════════════════════════════
   THE EXPERIENCE — Cinematic AI Intelligence
   One beautiful moment at a time.
   ═══════════════════════════════════════════════════════ */
function TheExperience({ onComplete }) {
  const [stage, setStage] = useState("dark");
  const [thoughts, setThoughts] = useState([]);
  const [discoveryLine, setDiscoveryLine] = useState(0);
  const [score, setScore] = useState(0);
  const [showSkip, setShowSkip] = useState(false);
  const [whyItems, setWhyItems] = useState([]);
  const [impactScores, setImpactScores] = useState([]);
  const timers = useRef([]);

  const finish = useCallback(() => {
    timers.current.forEach(clearTimeout);
    onComplete();
  }, [onComplete]);

  const t = (fn, ms) => { const id = setTimeout(fn, ms); timers.current.push(id); };

  useEffect(() => {
    // ═══════════════════════════════════════════════════
    // Timed for human reading speed: ~3 words/second.
    // Every message stays long enough to:
    // 1. Read it  2. Understand it  3. Feel it
    // Slow is premium. Rushed feels cheap.
    // ═══════════════════════════════════════════════════

    // ── 1. AI AWAKENS (0–5.5s) ────────────────────
    // 500ms pure black. Particles. Orbit. M draws. Init.
    t(() => setStage("particles"), 500);
    t(() => setStage("orbit"), 1800);
    t(() => setStage("draw-m"), 2800);
    t(() => setStage("init"), 4000);
    const initLines = ["Initializing Intelligence...", "Loading Vision Engine...", "Loading Design Intelligence...", "Ready."];
    initLines.forEach((txt, i) => { t(() => setThoughts(prev => [...prev, txt]), 4400 + i * 500); });
    t(() => setShowSkip(true), 4000);

    // ── 2. AI UNDERSTANDS (8–15s) ─────────────────
    // Each line: ~10 words = 3s to read + 0.5s pause = 3.5s per thought
    t(() => { setStage("understanding"); setThoughts([]); }, 8000);
    const understandLines = [
      "I'm looking at this the way a customer would.",         // 10 words
      "Studying the first impression...",                       // 4 words
      "Evaluating how visitors move through this page.",        // 8 words
      "I'm trying to understand why visitors leave.",           // 8 words
    ];
    // 1.5s between short lines, 2s between longer lines
    const understandDelays = [0, 2000, 3500, 5500];
    understandLines.forEach((txt, i) => {
      t(() => setThoughts(prev => [...prev, txt]), 8500 + understandDelays[i]);
    });

    // ── 3. THE DISCOVERY (16–22s) ─────────────────
    // Silence first. Then each line with generous breathing room.
    t(() => setStage("discovery"), 16000);
    t(() => setDiscoveryLine(1), 17000);   // "I think I understand why." — 6 words, 2s read
    t(() => setDiscoveryLine(2), 19500);   // "You're asking your website to do too much." — 9 words, 3s read
    t(() => setDiscoveryLine(3), 22500);   // "I'm fixing that now." — 5 words, but emotional weight

    // ── 4. THE REBUILD (24–29s) ───────────────────
    // Each piece appears with ~500ms spacing — visual rhythm
    t(() => { setStage("rebuild"); setThoughts([]); }, 24000);
    const rebuildLines = ["Foundation.", "Hero.", "Navigation.", "Typography.", "Images.", "Trust.", "CTA.", "Footer."];
    rebuildLines.forEach((txt, i) => { t(() => setThoughts(prev => [...prev, txt]), 24400 + i * 500); });

    // ── 5. BEFORE / AFTER (29.5–33s) ─────────────
    // 3.5 seconds to absorb the comparison
    t(() => setStage("beforeafter"), 29500);

    // ── 6. WHY IT'S BETTER (33–37s) ──────────────
    // Each improvement: ~600ms spacing
    t(() => setStage("why"), 33000);
    const whyLines = ["Stronger headline", "Better first impression", "More trust above the fold", "Cleaner navigation", "Stronger call-to-action", "Better mobile experience"];
    whyLines.forEach((txt, i) => { t(() => setWhyItems(prev => [...prev, txt]), 33400 + i * 600); });

    // ── 7. BUSINESS IMPACT (37.5–40s) ────────────
    // Each score: ~500ms, let each register
    t(() => setStage("impact"), 37500);
    const impacts = [
      { label: "Website", before: 42, after: 96 },
      { label: "Trust", before: 53, after: 95 },
      { label: "Conversion", before: 61, after: 98 },
      { label: "Mobile", before: 57, after: 99 },
    ];
    impacts.forEach((item, i) => { t(() => setImpactScores(prev => [...prev, item]), 38000 + i * 500); });

    // ── 8. FINAL THOUGHT (41–43s) ────────────────
    // "Now imagine what I could do for yours." — 9 words, 3s read + feel
    t(() => setStage("final"), 41000);

    // ── SETTLE (44s) ─────────────────────────────
    t(() => finish(), 44000);

    return () => timers.current.forEach(clearTimeout);
  }, [finish]);

  return (
    <div className="xp-container">
      <div className="xp-orb xp-orb-1" />
      <div className="xp-orb xp-orb-2" />

      <AnimatePresence>
        {showSkip && stage !== "impact" && stage !== "final" && (
          <motion.button className="xp-skip" onClick={finish} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="skip-intro">
            Skip Intro →
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">

        {/* DARK */}
        {stage === "dark" && <motion.div key="dark" className="xp-void" exit={{ opacity: 0, transition: { duration: 0.4 } }} />}

        {/* PARTICLES — tiny blue dots appear one at a time, begin orbiting */}
        {stage === "particles" && (
          <motion.div key="particles" className="xp-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.3 } }}>
            <div className="xp-orbit-field">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="xp-orbit-particle" style={{ animationDelay: `${i * 0.15}s`, transform: `rotate(${i * 60}deg) translateX(36px)` }} />
              ))}
            </div>
          </motion.div>
        )}

        {/* ORBIT — ring brightens, particles consolidate */}
        {stage === "orbit" && (
          <motion.div key="orbit" className="xp-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.3 } }}>
            <div className="xp-orbit-field xp-orbit-bright">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="xp-orbit-particle xp-orbit-particle-bright" style={{ animationDelay: `${i * 0.1}s`, transform: `rotate(${i * 45}deg) translateX(36px)` }} />
              ))}
              <div className="xp-orbit-ring-line" />
            </div>
          </motion.div>
        )}

        {/* DRAW M — SVG path draws itself stroke by stroke */}
        {stage === "draw-m" && (
          <motion.div key="draw-m" className="xp-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.3 } }}>
            <div className="xp-m-container">
              <svg viewBox="0 0 100 80" className="xp-m-svg" width="100" height="80">
                <defs>
                  <linearGradient id="m-grad" x1="0" y1="0" x2="100" y2="80" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#007AFF" />
                    <stop offset="100%" stopColor="#00E5FF" />
                  </linearGradient>
                </defs>
                <path
                  d="M10 70 L10 20 L50 50 L90 20 L90 70"
                  fill="none"
                  stroke="url(#m-grad)"
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="xp-m-path"
                />
              </svg>
              <div className="xp-m-glow" />
              <div className="xp-orbit-ring-complete" />
            </div>
          </motion.div>
        )}

        {/* INIT — M shrinks, thoughts begin */}
        {stage === "init" && (
          <motion.div key="init" className="xp-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.3 } }}>
            <div className="xp-m-container xp-m-small">
              <svg viewBox="0 0 100 80" className="xp-m-svg xp-m-svg-done" width="60" height="48">
                <defs><linearGradient id="m-grad2" x1="0" y1="0" x2="100" y2="80" gradientUnits="userSpaceOnUse"><stop offset="0%" stopColor="#007AFF" /><stop offset="100%" stopColor="#00E5FF" /></linearGradient></defs>
                <path d="M10 70 L10 20 L50 50 L90 20 L90 70" fill="none" stroke="url(#m-grad2)" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div className="xp-m-glow xp-m-glow-sm" />
            </div>
            <div className="xp-brand">ModernSite Intelligence</div>
            <div className="xp-thoughts">
              {thoughts.map((txt, i) => (
                <motion.div key={i} className={`xp-thought ${txt === "Ready." ? "xp-ready" : ""}`} initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}>
                  <span className="xp-check">✓</span>{txt}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* UNDERSTANDING */}
        {stage === "understanding" && (
          <motion.div key="understand" className="xp-center" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, transition: { duration: 0.3 } }} transition={{ duration: 0.5 }}>
            <div className="xp-stage-title">Understanding the website</div>
            <div className="xp-thoughts">
              {thoughts.map((txt, i) => (
                <motion.div key={i} className="xp-thought" initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}>
                  <span className="xp-check">✓</span>{txt}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* DISCOVERY — the emotional pivot */}
        {stage === "discovery" && (
          <motion.div key="disc" className="xp-center xp-discovery" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.2 } }}>
            {discoveryLine >= 1 && <motion.div className="xp-disc-quiet" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7 }}>I think I understand why.</motion.div>}
            {discoveryLine >= 2 && <motion.div className="xp-disc-main" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7 }}>You're asking your website to do too much.</motion.div>}
            {discoveryLine >= 3 && <motion.div className="xp-disc-ready" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>I'm fixing that now.</motion.div>}
          </motion.div>
        )}

        {/* REBUILD — piece by piece like an architect */}
        {stage === "rebuild" && (
          <motion.div key="rebuild" className="xp-center" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, transition: { duration: 0.3 } }} transition={{ duration: 0.4 }}>
            <div className="xp-stage-title">Rebuilding your experience</div>
            <div className="xp-rebuild-grid">
              {thoughts.map((txt, i) => (
                <motion.div key={i} className="xp-rebuild-item" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}>
                  {txt}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* BEFORE / AFTER — the payoff. Visual proof. */}
        {stage === "beforeafter" && (
          <motion.div key="ba" className="xp-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.3 } }}>
            <div className="xp-ba-container">
              <div className="xp-ba-side">
                <div className="xp-ba-label">Current Experience</div>
                <div className="xp-ba-card xp-ba-old">
                  <div className="xp-ba-mock-nav" />
                  <div className="xp-ba-mock-hero-old" />
                  <div className="xp-ba-mock-text-old" />
                  <div className="xp-ba-mock-text-old xp-ba-short" />
                  <div className="xp-ba-mock-btn-old" />
                </div>
              </div>
              <motion.div
                className="xp-ba-arrow"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >→</motion.div>
              <motion.div
                className="xp-ba-side"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
              >
                <div className="xp-ba-label xp-ba-label-new">ModernSite Experience</div>
                <div className="xp-ba-card xp-ba-new">
                  <div className="xp-ba-mock-nav xp-ba-nav-new" />
                  <div className="xp-ba-mock-hero-new" />
                  <div className="xp-ba-mock-text-new" />
                  <div className="xp-ba-mock-text-new xp-ba-short" />
                  <div className="xp-ba-mock-cards-new">
                    <div className="xp-ba-mc" /><div className="xp-ba-mc" /><div className="xp-ba-mc" />
                  </div>
                  <div className="xp-ba-mock-btn-new" />
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* SHOW WHY — educate the visitor */}
        {stage === "why" && (
          <motion.div key="why" className="xp-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.3 } }}>
            <div className="xp-why-list">
              {whyItems.map((txt, i) => (
                <motion.div key={i} className="xp-why-item" initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}>
                  <span className="xp-why-check">✓</span>{txt}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* BUSINESS IMPACT — scores appear one by one */}
        {stage === "impact" && (
          <motion.div key="impact" className="xp-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.3 } }}>
            <div className="xp-impact-grid">
              {impactScores.map((item, i) => (
                <motion.div key={i} className="xp-impact-card" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                  <div className="xp-impact-label">{item.label}</div>
                  <div className="xp-impact-nums">
                    <span className="xp-impact-before">{item.before}</span>
                    <span className="xp-impact-arrow">→</span>
                    <span className="xp-impact-after">{item.after}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* FINAL THOUGHT */}
        {stage === "final" && (
          <motion.div key="final" className="xp-center xp-discovery" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="xp-disc-final">Now imagine what I could do for yours.</div>
          </motion.div>
        )}

      </AnimatePresence>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   SETTLED — "I'm ready." → delete → "What website
   should I transform next?"
   ═══════════════════════════════════════════════════════ */
function SettledHero({ onAnalyze, url, setUrl }) {
  const [phase, setPhase] = useState("typing-ready");
  const [readyText, setReadyText] = useState("");
  const [questionText, setQuestionText] = useState("");
  const [showInput, setShowInput] = useState(false);

  useEffect(() => {
    const ready = "I'm ready.";
    const question = "What website should I transform next?";
    const timers = [];
    let idx = 0;

    // Type "I'm ready."
    ready.split("").forEach((_, i) => {
      timers.push(setTimeout(() => setReadyText(ready.slice(0, i + 1)), i * 50));
    });

    // Pause, then delete
    const deleteStart = ready.length * 50 + 800;
    ready.split("").forEach((_, i) => {
      timers.push(setTimeout(() => setReadyText(ready.slice(0, ready.length - i - 1)), deleteStart + i * 30));
    });

    // Switch to question
    const questionStart = deleteStart + ready.length * 30 + 400;
    timers.push(setTimeout(() => setPhase("typing-question"), questionStart));
    question.split("").forEach((_, i) => {
      timers.push(setTimeout(() => setQuestionText(question.slice(0, i + 1)), questionStart + i * 35));
    });

    // Show input after question typed
    timers.push(setTimeout(() => setShowInput(true), questionStart + question.length * 35 + 300));

    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <motion.div className="sh-settled" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
      {/* Ambient particles that never stop */}
      <div className="sh-ambient">
        {[...Array(5)].map((_, i) => <div key={i} className="sh-ambient-particle" style={{ animationDelay: `${i * 2}s`, left: `${20 + i * 15}%`, top: `${30 + (i % 3) * 20}%` }} />)}
      </div>

      {/* The living logo */}
      <div className="sh-logo-orbit">
        <div className="sh-logo-core" />
        <div className="sh-logo-ring" />
      </div>

      <div className="sh-typed-text">
        {phase === "typing-ready" && (
          <span className="sh-ready-text">{readyText}<span className="xp-cursor">|</span></span>
        )}
        {phase === "typing-question" && (
          <span className="sh-question-text">{questionText}<span className="xp-cursor">|</span></span>
        )}
      </div>

      <AnimatePresence>
        {showInput && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
            <form onSubmit={onAnalyze} className="sh-input-wrap">
              <div className="sh-input-container">
                <input data-testid="hero-url-input" className="sh-url-input" value={url} onChange={e => setUrl(e.target.value)} placeholder="Enter any website URL" autoFocus />
                <button data-testid="hero-cta-btn" type="submit" className="sh-go-btn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </button>
              </div>
            </form>
            <div className="sh-badges">
              <span className="sh-badge">Instant Analysis</span>
              <span className="sh-badge">AI-Powered</span>
              <span className="sh-badge">No Credit Card</span>
            </div>

            {/* ── PERMANENT BEFORE/AFTER PROOF ──────── */}
            <motion.div className="sh-proof" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.8 }}>
              <div className="sh-ba-row">
                <div className="sh-ba-side">
                  <div className="sh-ba-label">Current Experience</div>
                  <div className="sh-ba-card sh-ba-old">
                    <div className="sh-ba-nav" />
                    <div className="sh-ba-hero-old" />
                    <div className="sh-ba-line" /><div className="sh-ba-line sh-ba-line-s" />
                    <div className="sh-ba-btn-old" />
                  </div>
                </div>
                <div className="sh-ba-arrow">→</div>
                <div className="sh-ba-side">
                  <div className="sh-ba-label sh-ba-label-new">ModernSite Experience</div>
                  <div className="sh-ba-card sh-ba-new">
                    <div className="sh-ba-nav sh-ba-nav-new" />
                    <div className="sh-ba-hero-new" />
                    <div className="sh-ba-line-new" /><div className="sh-ba-line-new sh-ba-line-s" />
                    <div className="sh-ba-cards-row"><div className="sh-ba-mc" /><div className="sh-ba-mc" /><div className="sh-ba-mc" /></div>
                    <div className="sh-ba-btn-new" />
                  </div>
                </div>
              </div>

              {/* Impact scores below */}
              <div className="sh-impact-row">
                {[{l:"Website",b:42,a:96},{l:"Trust",b:53,a:95},{l:"Conversion",b:61,a:98},{l:"Mobile",b:57,a:99}].map((s,i) => (
                  <div key={i} className="sh-impact-pill">
                    <div className="sh-impact-lbl">{s.l}</div>
                    <div className="sh-impact-nums">
                      <span className="sh-impact-b">{s.b}</span>
                      <span className="sh-impact-arr">→</span>
                      <span className="sh-impact-a">{s.a}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════
   AI CREATIVE DIRECTOR — The Post-Submit Experience
   The AI visits, observes, reasons, rebuilds.
   ═══════════════════════════════════════════════════════ */
function AnalysisExperience({ url, onClose }) {
  const [stage, setStage] = useState("visiting");
  const [thoughts, setThoughts] = useState([]);
  const [scores, setScores] = useState(null);
  const [websiteId, setWebsiteId] = useState(null);
  const [scoreValue, setScoreValue] = useState(0);
  const [finalLine, setFinalLine] = useState(0);
  const timers = useRef([]);

  const tt = (fn, ms) => { const id = setTimeout(fn, ms); timers.current.push(id); };

  useEffect(() => {
    // ── Kick off real API calls in background ─────────
    axios.post(`${API}/leads/analyze-url?url=${encodeURIComponent(url)}`)
      .then(res => {
        const q = res.data.quality_details || {};
        setScores({ design:{old:Math.min((q.modern_design||8)+15+Math.floor(Math.random()*12),48),now:96}, mobile:{old:q.mobile_responsive?42+Math.floor(Math.random()*16):18+Math.floor(Math.random()*12),now:99}, speed:{old:25+Math.floor(Math.random()*20),now:97}, trust:{old:35+Math.floor(Math.random()*15),now:96} });
        return axios.post(`${API}/websites/generate`, { business_name:res.data.business_name||url, industry:"Local Business", keywords:res.data.description||"professional services", phone:res.data.phones?.[0]||"", email:res.data.emails?.[0]||"", theme:"dark", funnel_type:"conversion" });
      })
      .then(gen => setWebsiteId(gen.data.id))
      .catch(() => { setScores({ design:{old:34,now:96}, mobile:{old:41,now:99}, speed:{old:29,now:97}, trust:{old:40,now:96} }); });

    // ── ACT 1: VISITING (0-4s) ─────────────────────
    const visitLines = [
      "I'm visiting your website...",
      "I'm looking at it the way a customer would.",
    ];
    visitLines.forEach((txt, i) => {
      tt(() => setThoughts(prev => [...prev, txt]), 1000 + i * 1500);
    });

    // ── ACT 2: FIRST IMPRESSION (4-8s) ──────────
    tt(() => { setStage("impression"); setThoughts([]); }, 4500);
    const impressionLines = [
      "I'm studying your first impression.",
      "This headline isn't creating enough curiosity.",
      "Visitors won't immediately understand why this company is different.",
      "This first impression could be much stronger.",
    ];
    impressionLines.forEach((txt, i) => {
      tt(() => setThoughts(prev => [...prev, txt]), 5200 + i * 900);
    });

    // ── ACT 3: TRUST (9-13s) ─────────────────────
    tt(() => { setStage("trust"); setThoughts([]); }, 9500);
    const trustLines = [
      "I'm checking trust signals...",
      "Looking for reviews, guarantees, credentials...",
      "This image doesn't build trust.",
      "I don't see enough confidence-building elements above the fold.",
    ];
    trustLines.forEach((txt, i) => {
      tt(() => setThoughts(prev => [...prev, txt]), 10200 + i * 900);
    });

    // ── ACT 4: JOURNEY (14-17s) ──────────────────
    tt(() => { setStage("journey"); setThoughts([]); }, 14000);
    const journeyLines = [
      "I'm evaluating the customer journey...",
      "Landing → Reading → Looking for proof → Finding contact...",
      "This path requires too many decisions.",
      "I know how to fix this.",
    ];
    journeyLines.forEach((txt, i) => {
      tt(() => setThoughts(prev => [...prev, txt]), 14700 + i * 800);
    });

    // ── ACT 5: REBUILDING (18-21s) ────────────────
    tt(() => setStage("rebuilding"), 18500);

    // ── ACT 6: SCORES (21-24s) ──────────────────
    tt(() => {
      setStage("scores");
      const steps = [42, 50, 60, 71, 80, 88, 93, 96];
      steps.forEach((s, i) => { tt(() => setScoreValue(s), i * 280); });
    }, 21500);

    // ── ACT 7: COMPLETE (25-30s) ──────────────────
    tt(() => setStage("complete"), 25000);
    tt(() => setFinalLine(1), 25800);  // "I think your customers will like this much better."
    tt(() => setFinalLine(2), 28000);  // Scores + "View your new website."

    return () => timers.current.forEach(clearTimeout);
  }, [url]);

  const viewRedesign = async () => {
    if (!websiteId) { toast.info("Still generating — one moment..."); return; }
    for (let i = 0; i < 30; i++) {
      try {
        const s = await axios.get(`${API}/websites/${websiteId}/status`);
        if (s.data.status === "ready") { window.open(`${API}/websites/${websiteId}/html`, "_blank"); onClose(); return; }
        if (s.data.status === "failed") { toast.error("Generation failed"); return; }
      } catch (e) {}
      await new Promise(r => setTimeout(r, 2000));
    }
  };

  return (
    <motion.div className="ms-analysis-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <button className="ms-close-btn" onClick={onClose} data-testid="analysis-close">&times;</button>
      <div className="ms-analysis-inner">

        {/* URL always visible */}
        <div className="ai-url">{url}</div>

        <AnimatePresence mode="wait">

          {/* VISITING */}
          {stage === "visiting" && (
            <motion.div key="visit" className="ai-stage" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.4 } }}>
              <div className="ai-thoughts">
                {thoughts.map((t, i) => (
                  <motion.div key={i} className="ai-line" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>{t}</motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* FIRST IMPRESSION */}
          {stage === "impression" && (
            <motion.div key="impression" className="ai-stage" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, transition: { duration: 0.4 } }}>
              <div className="ai-thoughts">
                {thoughts.map((t, i) => (
                  <motion.div key={i} className={`ai-line ${i >= 2 ? "ai-observation" : ""}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>{t}</motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* TRUST */}
          {stage === "trust" && (
            <motion.div key="trust" className="ai-stage" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, transition: { duration: 0.4 } }}>
              <div className="ai-thoughts">
                {thoughts.map((t, i) => (
                  <motion.div key={i} className={`ai-line ${i >= 2 ? "ai-observation" : ""}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>{t}</motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* JOURNEY */}
          {stage === "journey" && (
            <motion.div key="journey" className="ai-stage" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, transition: { duration: 0.4 } }}>
              <div className="ai-thoughts">
                {thoughts.map((t, i) => (
                  <motion.div key={i} className={`ai-line ${t === "I know how to fix this." ? "ai-ready" : i === 1 ? "ai-path" : ""}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>{t}</motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* REBUILDING */}
          {stage === "rebuilding" && (
            <motion.div key="rebuild" className="ai-stage" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.3 } }}>
              <div className="ai-rebuild-text">I'm rebuilding your experience.</div>
              <div className="ai-rebuild-bar"><div className="ai-rebuild-fill" /></div>
            </motion.div>
          )}

          {/* SCORES */}
          {stage === "scores" && (
            <motion.div key="scores" className="ai-stage" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.3 } }}>
              <div className="ai-score-num">{scoreValue}</div>
              <div className="ai-score-cap">Website Score</div>
            </motion.div>
          )}

          {/* COMPLETE */}
          {stage === "complete" && (
            <motion.div key="complete" className="ai-stage" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {finalLine >= 1 && (
                <motion.div className="ai-final-line" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
                  I think this represents the business much better.
                </motion.div>
              )}
              {finalLine >= 2 && (
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}>
                  <div className="ai-final-proud">I'd be proud to put my name on this.</div>
                  <div className="ai-scores-row">
                    {scores && Object.entries(scores).map(([key, val], i) => (
                      <motion.div key={key} className="ai-score-pill" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 + i * 0.12 }}>
                        <div className="ai-pill-label">{key}</div>
                        <div className="ai-pill-before">{val.old}</div>
                        <div className="ai-pill-arrow">→</div>
                        <div className="ai-pill-after">{val.now}</div>
                      </motion.div>
                    ))}
                  </div>
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}>
                    <button className="ms-result-cta" onClick={viewRedesign} data-testid="claim-website-btn" style={{ marginTop: 32 }}>
                      Would you like to see yours? →
                    </button>
                  </motion.div>
                </motion.div>
              )}
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════ */
export default function LandingPage() {
  const [url, setUrl] = useState("");
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [experienceComplete, setExperienceComplete] = useState(false);
  const [navVisible, setNavVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => { if (experienceComplete) setNavVisible(window.scrollY > 10); };
    window.addEventListener("scroll", onScroll);
    const obs = new IntersectionObserver(entries => { entries.forEach(e => { if (e.isIntersecting) e.target.classList.remove("hid"); }); }, { threshold: 0.06 });
    setTimeout(() => document.querySelectorAll(".ms-rv").forEach(el => { el.classList.add("hid"); obs.observe(el); }), 200);
    return () => { window.removeEventListener("scroll", onScroll); obs.disconnect(); };
  }, [experienceComplete]);

  const handleComplete = useCallback(() => { setExperienceComplete(true); setNavVisible(true); }, []);
  const handleSubmit = (e) => { e.preventDefault(); if (!url.trim()) { toast.error("Enter a website URL"); return; } setShowAnalysis(true); };

  return (
    <div className="ms">
      <AnimatePresence>
        {navVisible && (
          <motion.nav className="ms-nav scrolled" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}>
            <div className="ms-nav-logo">ModernSite<span>.ai</span></div>
            <div className="ms-nav-links">
              <a href="#how-it-works">How It Works</a>
              <a href="#transformations">Transformations</a>
              <a href="#pricing">Pricing</a>
              <a href="#faq">FAQ</a>
              <a href="#top" className="ms-nav-cta" onClick={e => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}>Get Your New Website</a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      <section className="ms-hero" id="top">
        {!experienceComplete ? <TheExperience onComplete={handleComplete} /> : <SettledHero onAnalyze={handleSubmit} url={url} setUrl={setUrl} />}
      </section>

      <section id="how-it-works" className="ms-section">
        <div className="ms-section-inner" style={{ textAlign: "center" }}>
          <div className="ms-rv"><div className="ms-overline">How it works</div><h2 className="ms-h2">Intelligence, not <em>templates.</em></h2></div>
          <div className="ms-steps">
            {[{ n:"01", t:"Enter any URL", d:"Paste a website. ModernSite AI begins scanning instantly." },{ n:"02", t:"AI analyzes everything", d:"Design, mobile, speed, SEO, conversion, trust — all scored live." },{ n:"03", t:"Watch it transform", d:"The old site dissolves. A premium redesign appears with real metrics." },{ n:"04", t:"Go live in 24 hours", d:"Approve the design. We handle hosting, domain, SSL, and launch." }].map((s, i) => (
              <div key={i} className="ms-step-card ms-rv" style={{ transitionDelay: `${i * 0.1}s` }}><div className="ms-step-num">{s.n}</div><div className="ms-step-title">{s.t}</div><div className="ms-step-desc">{s.d}</div></div>
            ))}
          </div>
        </div>
      </section>

      <section id="transformations" className="ms-section" style={{ background: "var(--bg2)" }}>
        <div className="ms-section-inner">
          <div className="ms-rv"><div className="ms-overline">AI Transformations</div><h2 className="ms-h2">Real businesses. <em>Real results.</em></h2></div>
          <div className="ms-gallery-grid">
            {GALLERY_ITEMS.map((item, i) => (
              <div key={i} className="ms-gallery-card ms-rv" style={{ transitionDelay: `${i * 0.08}s` }} data-testid={`gallery-card-${i}`}>
                <div className="ms-gallery-card-inner"><div className="ms-gallery-tag">{item.tag}</div><div><div className="ms-gallery-name">{item.name}</div><div className="ms-gallery-industry">{item.industry}</div><div className="ms-gallery-scores"><span className="ms-gallery-before">{item.before}</span><span className="ms-gallery-arrow">→</span><span className="ms-gallery-after">{item.after}</span><span className="ms-gallery-metric">{item.metric}</span></div></div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ms-section">
        <div className="ms-section-inner" style={{ textAlign: "center" }}>
          <div className="ms-rv"><div className="ms-overline">Business Impact</div><h2 className="ms-h2">Measurable <em>growth.</em></h2><p className="ms-section-sub" style={{ margin: "0 auto" }}>Every transformation comes with a full intelligence report.</p></div>
          <div className="ms-report-grid ms-rv">
            <div className="ms-report-card"><div className="ms-report-metric-label">Website Score</div><div className="ms-report-metric-value">96</div><div className="ms-report-metric-delta">↑ from 34</div></div>
            <div className="ms-report-card"><div className="ms-report-metric-label">Trust Score</div><div className="ms-report-metric-value">95</div><div className="ms-report-metric-delta">↑ from 42</div></div>
            <div className="ms-report-card"><div className="ms-report-metric-label">Mobile Score</div><div className="ms-report-metric-value">99</div><div className="ms-report-metric-delta">↑ from 51</div></div>
            <div className="ms-report-card featured"><div><div className="ms-report-metric-label">Est. New Leads</div><div className="ms-report-metric-value">+38%</div></div><div><div className="ms-report-metric-label">Revenue Opportunity</div><div className="ms-report-metric-value">+$126k</div><div className="ms-report-metric-delta">per year · AI estimate</div></div><div><div className="ms-report-metric-label">Conversion Lift</div><div className="ms-report-metric-value">+42%</div></div></div>
          </div>
        </div>
      </section>

      <section id="pricing" className="ms-section" style={{ background: "var(--bg2)" }}>
        <div className="ms-section-inner" style={{ textAlign: "center" }}>
          <div className="ms-rv"><div className="ms-overline">Investment</div><h2 className="ms-h2">One new customer <em>pays this back.</em></h2></div>
          <div className="ms-pricing-grid ms-rv">
            <div className="ms-price-card"><div className="ms-price-name">Starter</div><div className="ms-price-amount">$499</div><div className="ms-price-period">one-time</div><ul className="ms-price-list"><li>Premium one-page website</li><li>Mobile optimized</li><li>SEO architecture</li><li>Call, book, text CTAs</li><li>EN/ES bilingual</li><li>SSL & hosting setup</li><li>1 round of revisions</li></ul><a href="#top" className="ms-price-cta ms-price-cta-outline">See My Redesign</a></div>
            <div className="ms-price-card featured"><div className="ms-price-badge">Recommended</div><div className="ms-price-name">Growth</div><div className="ms-price-amount">$499 <span>+ $49/mo</span></div><div className="ms-price-period">setup + managed</div><ul className="ms-price-list"><li>Everything in Starter</li><li>Managed hosting & SSL</li><li>Monthly edits</li><li>Priority support</li><li>Analytics dashboard</li><li>Google Business Profile</li><li>Ongoing SEO</li></ul><a href="#top" className="ms-price-cta ms-price-cta-fill">See My Redesign</a></div>
          </div>
        </div>
      </section>

      <section id="faq" className="ms-section">
        <div className="ms-section-inner">
          <div className="ms-rv"><div className="ms-overline">FAQ</div><h2 className="ms-h2">Questions</h2></div>
          <div className="ms-faq">
            {[{ q:"Do I keep my domain?", a:"Yes. We connect your new site to your existing domain." },{ q:"How fast is setup?", a:"Preview in 60 seconds. Full launch in 24-48 hours." },{ q:"Can I request changes?", a:"Starter includes one round. Growth includes unlimited monthly edits." },{ q:"Is hosting included?", a:"Yes. Hosting, SSL, and maintenance are covered." },{ q:"Will SEO be affected?", a:"It improves. Every site is built with modern SEO architecture." }].map((f,i) => (<details key={i} className="ms-rv" data-testid={`faq-${i}`}><summary>{f.q}</summary><p>{f.a}</p></details>))}
          </div>
        </div>
      </section>

      <section className="ms-final-cta">
        <div className="ms-glow ms-glow-1" style={{ bottom:"-200px", top:"auto" }} />
        <div style={{ position:"relative", zIndex:2 }}>
          <h2 className="ms-rv">Your website is the<br /><strong>first impression.</strong><br /><em>Make it unforgettable.</em></h2>
          <p className="ms-final-sub">Enter your URL. Watch the transformation. Decide after.</p>
          <form onSubmit={handleSubmit} className="ms-input-wrap" style={{ maxWidth:480, margin:"0 auto" }}>
            <div className="ms-url-input-container"><input data-testid="final-url-input" className="ms-url-input" value={url} onChange={e => setUrl(e.target.value)} placeholder="yourwebsite.com" /><button data-testid="final-cta-btn" type="submit" className="ms-submit-btn">Analyze</button></div>
          </form>
        </div>
      </section>

      <footer className="ms-footer">
        <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:17, fontWeight:700, marginBottom:12 }}>ModernSite<span style={{ opacity:0.4 }}>.ai</span></div>
        <div className="ms-footer-links"><a href="#transformations">Transformations</a><a href="#how-it-works">Process</a><a href="#pricing">Pricing</a></div>
        <p className="ms-footer-copy">© {new Date().getFullYear()} ModernSiteAI</p>
      </footer>

      {showAnalysis && <AnalysisExperience url={url} onClose={() => setShowAnalysis(false)} />}
    </div>
  );
}
